// packages/shared/data/auth.js
import axios from '../config/axios';

/**
 * Unified login used by Web + Mobile + Notes.
 *
 * Returns backend response, which may be:
 * - { token, userId, ... } OR (future) { accessToken, refreshToken, ... }
 * - { require2fa: true, challengeToken, methods? }
 *
 * Throws a friendly Error message on failure.
 */
export const login = async (email, password) => {
  try {
    const normalizedEmail = String(email || '')
      .trim()
      .toLowerCase();

    const res = await axios.post('/login', {
      email: normalizedEmail,
      password,
    });
    const data = res?.data || {};

    // Accept the existing 2FA shape you already use in both web & mobile:
    //   require2fa + challengeToken
    // Accept token-based success (current)
    // We'll later extend to accessToken/refreshToken without changing callers.
    const hasToken = typeof data.token === 'string' && data.token.length > 0;
    const has2faChallenge =
      data.require2fa === true &&
      typeof data.challengeToken === 'string' &&
      data.challengeToken.length > 0;

    if (!hasToken && !has2faChallenge) {
      // This protects you from silent backend contract drift
      console.warn('[auth] Unexpected /login response:', data);
      throw new Error('Login failed: unexpected response from server.');
    }

    return data;
  } catch (err) {
    const apiError = err?.response?.data?.error;

    if (apiError) {
      throw new Error(apiError);
    }

    // Common axios/network fallback
    if (err?.message) {
      throw new Error(err.message);
    }

    throw new Error('Login failed. Please try again.');
  }
};

export const fetchSharedUsers = async (email, token) => {
  const normalizedEmail = String(email || '')
    .trim()
    .toLowerCase();

  const res = await axios.put(
    '/users/shared',
    { email: normalizedEmail },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const resendVerification = async (email) => {
  const res = await axios.post('/auth/resend-verification', { email });
  return res.data;
};
